/**
 * /dashboard/leave/$id — Leave-request detail (SOP §1.2;
 * Phase 3b — 2026-06-01).
 *
 * Wave 2: the request is read from GET /leave/requests/{id}
 * (useLeaveRequest). It used to be DERIVED from the caller's role-scoped
 * list, so anyone that list did not include — a team lead holding the
 * employee role, an out-of-office delegate, an approver whose list was
 * paged — opened a notification and saw "Request not found". The server
 * authorises the read (owner / HR / approver chain / live delegate /
 * decider; 404 otherwise) and returns can_decide / can_self_approve and
 * who the request is waiting on. There is still no leave-timeline table.
 *
 * The "timeline" here is NOT a synthetic event feed — it is the
 * request's own lifecycle fields (created_at → status, and if decided:
 * decided_by_id / decided_at / decision_notes) presented as an honest
 * status & decision summary. Approve/reject (approver) and cancel
 * (requester) are state+role gated in LeaveDecisionActions; the
 * backend is the boundary.
 *
 * docs/67 §6: the detail shows what the list already did — a half-day
 * request's half ("First half") instead of a date range, and "Applied by HR
 * (on behalf)" with the filer when HR filed it for the employee.
 *
 * Migration note: port of
 * frontend/src/app/(dashboard)/dashboard/leave/[id]/page.tsx. Swaps:
 *   - "use client" dropped
 *   - use(params) (Next.js) → Route.useParams() (TanStack)
 *   - next/link → @tanstack/react-router Link (typed)
 */

import { useMemo } from "react";
import { createFileRoute, Link, useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft, CalendarRange, FileText, UserCog } from "lucide-react";
import { useLeaveTypes } from "@/hooks/use-leave";
import { isLeaveNotFound, useLeaveRequest } from "@/hooks/use-leave-request";
import { useDirectory } from "@/hooks/use-directory";
import { toApiError } from "@/lib/api";
import {
  fmtDate,
  fmtDateRange,
  fmtDateTime,
  fmtDays,
  halfDayLabel,
} from "@/lib/leave-format";
import { useAuth } from "@/lib/auth";
import { TimeAndLeaveShell } from "@/components/shared/time-and-leave-shell";
import { StatusBadge } from "@/components/shared/status-badge";
import { LeaveFeatureGate } from "@/components/leave/leave-feature-gate";
import { LeaveDecisionActions } from "@/components/leave/leave-decision-actions";
import { LeaveApprovalContext } from "@/components/leave/leave-approval-context";
import { RequestSurfaceClaim } from "@/components/dashboard/surface-claim";
import { fromSearchSchema } from "@/lib/from-search";
import { decidedByLabel, decidedElsewhereLine } from "@/lib/approval-copy";
import { requestDetailBack } from "@/lib/request-detail-nav";
import { pendingDecisionLine } from "@/lib/leave-cancel-copy";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/leave/$id")({
  // ?from=me|manage|inbox — the side that opened it (lib/from-search.ts).
  validateSearch: fromSearchSchema,
  component: LeaveRequestDetailRoute,
});

function Row({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-md border border-input px-3 py-2">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="text-right text-sm font-medium text-gray-900">
        {value}
      </dd>
    </div>
  );
}

function LeaveRequestDetailRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { id } = Route.useParams();
  // A malformed id ("foo") parses to NaN; the query is disabled and the
  // page falls through to the "Request not found" panel — the same UX as
  // a deleted / not-visible request (the server 404s both, no oracle).
  const numId = Number(id);
  const validId = Number.isFinite(numId) && numId > 0;
  const { user } = useAuth();
  const q = useLeaveRequest(numId);
  const request = q.data;
  const isResolving = validId && q.isPending;
  const notFound = !validId || (q.isError && isLeaveNotFound(q.error));
  const isError = q.isError && !notFound;
  const typesQ = useLeaveTypes();
  // Fallback id→name resolver; the request's own *_name is preferred.
  const dir = useDirectory({ includeInactive: true });

  // Back follows ?from= (Inbox / Manage / Me); someone else's request
  // opened from a notification goes back to the Inbox (persona round).
  // A decision taken from the Inbox returns there (lib/request-detail-nav).
  const { from } = Route.useSearch();
  const navigate = useNavigate();
  const back = requestDetailBack({
    kind: "leave",
    from,
    isOwner: request && user ? request.user_id === user.user_id : null,
  });
  const onDecided = back.returnToInbox
    ? () => void navigate({ to: back.to, params: { tenantSlug }, search: back.search })
    : undefined;
  const decidedElsewhere = request
    ? decidedElsewhereLine({
        status: request.status,
        deciderId: request.decided_by_id,
        deciderName: dir.label(request.decided_by_id, request.decided_by_name),
        decidedAtLabel: request.decided_at ? fmtDateTime(request.decided_at) : null,
        viewerId: user?.user_id,
        ownerId: request.user_id,
      })
    : null;

  const typeLabel = useMemo(() => {
    if (!request) return "";
    const t = typesQ.data?.leave_types.find(
      (x) => x.id === request.leave_type_id,
    );
    return t
      ? `${t.name} (${t.code})`
      : `Leave type #${request.leave_type_id}`;
  }, [request, typesQ.data]);

  return (
    <TimeAndLeaveShell
      // The record, so the breadcrumb and the tab title name it:
      // "Leave Request — Meera Joshi, 3–5 Nov 2026" (no name on your own).
      title={
        request
          ? `Leave Request — ${
              request.user_id !== user?.user_id
                ? `${dir.label(request.user_id, request.user_name, false)}, `
                : ""
            }${fmtDateRange(request.start_date, request.end_date)}`
          : "Leave Request"
      }
      description={
        request
          ? `Filed on ${fmtDate(request.created_at)}.`
          : undefined
      }
    >
      <LeaveFeatureGate>
        {/* Phones only: the desktop breadcrumb goes to the same place. */}
        <div className="mb-4 flex justify-end lg:hidden">
          <Button asChild variant="outline" className="gap-1.5">
            <Link to={back.to} params={{ tenantSlug }} search={back.search}>
              <ArrowLeft className="h-4 w-4" />
              {back.label}
            </Link>
          </Button>
        </div>

        {isResolving && (
          <div className="space-y-3">
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        )}

        {isError && (
          <Alert variant="destructive">
            <AlertTitle>Could not load this request</AlertTitle>
            <AlertDescription>
              {toApiError(q.error).message}
            </AlertDescription>
          </Alert>
        )}

        {notFound && (
          <Alert>
            <AlertTitle>Request not found</AlertTitle>
            <AlertDescription>
              This leave request doesn&apos;t exist or isn&apos;t
              visible to your role.{" "}
              <Link
                to="/app/$tenantSlug/leave" params={{ tenantSlug }}
                className="font-medium text-primary underline-offset-4 hover:underline"
              >
                Back to Leave
              </Link>
              .
            </AlertDescription>
          </Alert>
        )}

        {/* An approver viewing someone's request stays on Inbox (QA #15). */}
        {request && <RequestSurfaceClaim ownerId={request.user_id} />}
        {request && (
          <div className="space-y-5">
            {decidedElsewhere && (
              <Alert>
                <AlertTitle>Already decided</AlertTitle>
                <AlertDescription>{decidedElsewhere}</AlertDescription>
              </Alert>
            )}
            <Card>
              <CardHeader>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <CardTitle className="text-base">
                      {typeLabel} · {fmtDays(request.total_days)}
                    </CardTitle>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {request.user_id === user?.user_id
                        ? "Your request"
                        : dir.label(request.user_id, request.user_name)}
                      {request.on_behalf && (
                        <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-gray-700">
                          <UserCog className="h-3 w-3" />
                          Applied by HR (on behalf)
                        </span>
                      )}
                    </p>
                  </div>
                  <StatusBadge status={request.status} />
                </div>
              </CardHeader>
              <CardContent>
                <dl className="grid gap-3 sm:grid-cols-2">
                  <Row
                    label={request.half_day ? "Date" : "Dates"}
                    value={
                      <span className="inline-flex items-center gap-1.5">
                        <CalendarRange className="h-3.5 w-3.5 text-muted-foreground" />
                        {request.half_day ? (
                          <>
                            {fmtDate(request.start_date)} ·{" "}
                            {halfDayLabel(request.half_day)}
                          </>
                        ) : (
                          fmtDateRange(request.start_date, request.end_date)
                        )}
                      </span>
                    }
                  />
                  <Row
                    label="Working Days"
                    value={fmtDays(request.total_days)}
                  />
                  <Row
                    label="Submitted"
                    value={fmtDateTime(request.created_at)}
                  />
                  <Row
                    label="Filed By"
                    value={
                      request.on_behalf
                        ? `Applied by HR (on behalf) — ${
                            request.created_by_id != null
                              ? dir.label(
                                  request.created_by_id,
                                  request.created_by_name,
                                )
                              : (request.created_by_name ?? "HR")
                          }`
                        : request.user_id === user?.user_id
                          ? "You"
                          : "The employee"
                    }
                  />
                  <Row label="Type" value={typeLabel} />
                </dl>
                {request.reason && (
                  <div className="mt-4 rounded-md border border-input bg-muted/30 p-3">
                    <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                      <FileText className="h-3.5 w-3.5" />
                      Reason
                    </p>
                    <p className="mt-1 whitespace-pre-wrap text-sm text-gray-900">
                      {request.reason}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Status & decision summary — the honest "timeline": the
                request's own lifecycle fields, NOT a synthetic feed. */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  Status &amp; Decision
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <dl className="grid gap-3 sm:grid-cols-2">
                  <Row
                    label="Current Status"
                    value={<StatusBadge status={request.status} />}
                  />
                  {request.decided_at ? (
                    <>
                      <Row
                        label="Decided"
                        value={fmtDateTime(request.decided_at)}
                      />
                      <Row
                        label="Decided By"
                        value={
                          request.decided_by_id != null
                            ? decidedByLabel(
                                dir.label(
                                  request.decided_by_id,
                                  request.decided_by_name,
                                ),
                                request.decided_by_id,
                                request.user_id,
                                request.auto_approved === true,
                              )
                            : "—"
                        }
                      />
                      <Row
                        label="Decision Note"
                        value={request.decision_notes || "—"}
                      />
                    </>
                  ) : (
                    <div className="sm:col-span-2">
                      <dt className="sr-only">Decision</dt>
                      <dd className="rounded-md border border-dashed border-input px-3 py-2 text-sm text-muted-foreground">
                        {request.status === "pending"
                          ? pendingDecisionLine(request.awaiting_approver_names ?? [])
                          : request.status === "cancelled"
                            ? "This request was cancelled before any decision."
                            : "No decision recorded."}
                      </dd>
                    </div>
                  )}
                </dl>

                {request.can_decide && request.status === "pending" && (
                  <LeaveApprovalContext request={request} typeName={typeLabel} />
                )}

                <div className="pt-1">
                  <LeaveDecisionActions
                    request={request}
                    canDecide={request.can_decide}
                    canSelfApprove={request.can_self_approve}
                    onDecided={onDecided}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </LeaveFeatureGate>
    </TimeAndLeaveShell>
  );
}
